import React, { useEffect, useState } from "react";
import { FileTextIcon, KanbanIcon, NotePencilIcon, SparkleIcon, XIcon } from "@phosphor-icons/react";
import { Pressable, ScrollView, StyleSheet, View, type PressableStateCallbackType } from "react-native";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { ModernizerCreateActivityInput } from "./ModernizerAddActivityTypes";
import ModernizerIleActivityForm from "./ModernizerIleActivityForm";
import ModernizerPageActivityForm from "./ModernizerPageActivityForm";
import ModernizerProjectActivityForm from "./ModernizerProjectActivityForm";
import {
  getModernizerLmsActivityCapabilities,
  type ModernizerActivityCapability,
  type ModernizerActivityType,
} from "../utils/modernizerActivityCapabilities";

interface ModernizerAddActivityDrawerProps {
  open: boolean;
  moduleTitle: string;
  isSubmitting: boolean;
  onAddAssignment?: () => void;
  onSave: (activity: ModernizerCreateActivityInput) => void;
  onClose: () => void;
}

function getActivityIcon(type: ModernizerActivityType) {
  switch (type) {
    case "assignment":
      return <NotePencilIcon size={20} color={colors.grey[500]} />;
    case "page":
      return <FileTextIcon size={20} color={colors.grey[500]} />;
    case "project":
      return <KanbanIcon size={20} color={colors.grey[500]} />;
    case "ile":
      return <SparkleIcon size={20} color={colors.grey[500]} />;
  }
}

function ActivityTypeCard({
  capability,
  onPress,
}: {
  capability: ModernizerActivityCapability;
  onPress: (type: ModernizerActivityType) => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Add ${capability.title}`}
      onPress={() => onPress(capability.type)}
      style={({ hovered, pressed }: PressableStateCallbackType) => [
        styles.activityCard,
        hovered && styles.activityCardHover,
        pressed && styles.activityCardPressed,
      ]}>
      <View style={styles.activityIconWrap}>{getActivityIcon(capability.type)}</View>
      <CustomText text={capability.title} useTranslationText={false} size="s" weight="bold" style={styles.cardTitle} />
      <CustomText
        text={capability.description}
        useTranslationText={false}
        size="xs"
        weight="normal"
        style={styles.cardDescription}
      />
    </Pressable>
  );
}

function ModernizerAddActivityDrawer({
  open,
  moduleTitle,
  isSubmitting,
  onAddAssignment,
  onSave,
  onClose,
}: ModernizerAddActivityDrawerProps) {
  const [selectedType, setSelectedType] = useState<ModernizerActivityType | null>(null);
  const capabilities = getModernizerLmsActivityCapabilities();

  useEffect(() => {
    if (!open) {
      setSelectedType(null);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const handleClose = () => {
    if (isSubmitting) return;
    setSelectedType(null);
    onClose();
  };

  const handleSelectType = (type: ModernizerActivityType) => {
    if (type === "assignment") {
      onAddAssignment?.();
      return;
    }
    setSelectedType(type);
  };

  const renderSelectedForm = () => {
    switch (selectedType) {
      case "page":
        return (
          <ModernizerPageActivityForm
            isSubmitting={isSubmitting}
            onBack={() => setSelectedType(null)}
            onCancel={handleClose}
            onSave={onSave}
          />
        );
      case "ile":
        return (
          <ModernizerIleActivityForm
            isSubmitting={isSubmitting}
            onBack={() => setSelectedType(null)}
            onCancel={handleClose}
            onSave={onSave}
          />
        );
      case "project":
        return (
          <ModernizerProjectActivityForm
            isSubmitting={isSubmitting}
            onBack={() => setSelectedType(null)}
            onCancel={handleClose}
            onSave={onSave}
          />
        );
      default:
        return (
          <>
            <View style={styles.drawerHeader}>
              <CustomText
                text={`Add activity to ${moduleTitle}`}
                useTranslationText={false}
                size="l"
                weight="bold"
                style={styles.drawerTitle}
              />
              <Pressable accessibilityRole="button" accessibilityLabel="Close add activity" onPress={handleClose}>
                <XIcon size={20} color={colors.grey[600]} />
              </Pressable>
            </View>
            <ScrollView style={styles.drawerBody} contentContainerStyle={styles.chooserContent} tabIndex={0}>
              <CustomText
                text="Select the type of activity you want to create. You can change content details on the next step."
                useTranslationText={false}
                size="s"
                style={styles.chooserIntro}
              />
              <View style={styles.cardGrid}>
                {capabilities.map((capability) => (
                  <ActivityTypeCard key={capability.type} capability={capability} onPress={handleSelectType} />
                ))}
              </View>
            </ScrollView>
          </>
        );
    }
  };

  return (
    <View style={styles.overlay}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Close add activity"
        style={styles.backdrop}
        onPress={handleClose}
      />
      <View style={styles.sheet}>{renderSelectedForm()}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 25,
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.24)",
  },
  sheet: {
    width: 760,
    maxWidth: "100%",
    height: "100%",
    backgroundColor: colors.white,
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[200],
  },
  drawerHeader: {
    minHeight: 64,
    paddingHorizontal: 28,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  drawerTitle: {
    color: colors.grey[900],
    flex: 1,
    paddingRight: 16,
  },
  drawerBody: {
    flex: 1,
  },
  chooserContent: {
    padding: 28,
    paddingBottom: 40,
  },
  chooserIntro: {
    color: colors.grey[700],
    lineHeight: 22,
    marginBottom: 24,
  },
  cardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  activityCard: {
    width: "calc(50% - 8px)",
    minHeight: 190,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    gap: 10,
  },
  activityCardHover: {
    borderColor: colors.grey[300],
    backgroundColor: colors.grey[50],
    transform: [{ translateY: -1 }],
  },
  activityCardPressed: {
    borderColor: colors.brand[300],
    backgroundColor: "#F5F0FE",
  },
  activityIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.grey[50],
  },
  cardTitle: {
    color: colors.grey[900],
  },
  cardDescription: {
    color: colors.grey[600],
    lineHeight: 20,
  },
});

export default ModernizerAddActivityDrawer;
